import React from "react";
import { useTranslation } from "react-i18next";
import {
    Grid,
    Filter,
    useBaseAppContext,
    useResourceApi,
    numberFormat,
    numberFormatCurrency,
} from "@programari-limit/base-react";
import {
    Box,
    Divider,
    Icon,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import GridFilterField from "components/GridFilterField";
import MuiGrid from "@mui/material/Grid";
import { SpringFilterQueryBuilder } from "utils/spring-filter-query-builder";
import {
    columnesArticle,
    columnesTripulant,
} from "sharedAdvancedSearch/advancedSearchColumns";
import { suffixValueFormatter } from "utils/muiUtils";
import { useNavigate } from "react-router";

const FilterContent = (props) => {
    const { filterApiRef } = props;
    const { t } = useTranslation();

    return (
        <MuiGrid container spacing={2}>
            <GridFilterField name="dataInici" xs={6} sm={6} md={3} lg={1.5} />
            <GridFilterField name="dataFi" xs={6} sm={6} md={3} lg={1.5} />
            <GridFilterField
                name="article"
                xs={12}
                sm={6}
                md={6}
                lg={2}
                advancedSearchColumns={columnesArticle}
                advancedSearchMaxWidth={"lg"}
            />
            <GridFilterField name="recurs" xs={12} sm={6} md={4} lg={1.75} />
            <GridFilterField
                name="embarcacio"
                xs={12}
                sm={6}
                md={4}
                lg={1.75}
            />
            <GridFilterField
                name="tripulant"
                xs={12}
                sm={6}
                md={4}
                lg={2}
                advancedSearchColumns={columnesTripulant}
            />
            <GridFilterField
                name="consumPagat"
                xs={9}
                sm={6}
                md={3}
                lg={1}
                type="checkbox-select"
            />
            <MuiGrid item lg={0.5}>
                <Tooltip title={t("comu.resetFiltre")}>
                    <IconButton
                        onClick={() => filterApiRef?.current?.doReset()}
                    >
                        <Icon>filter_alt_off</Icon>
                    </IconButton>
                </Tooltip>
            </MuiGrid>
        </MuiGrid>
    );
};

const springFilterBuilder = (data) => {
    const { and, equal, ge, le, exists } = SpringFilterQueryBuilder;

    const filter = and(
        equal("tipusOperacio", "CONSUM_TRIPULACIO"),
        ge("data", data?.dataInici ? data?.dataInici + "T00:00:00" : undefined),
        le("data", data?.dataFi ? data?.dataFi + "T23:59:59" : undefined),
        equal("embarcacio.id", data?.embarcacio?.id),
        equal("consumidorTripulant.id", data?.tripulant?.id),
        equal("ruta.recurs.id", data?.ruta?.id),
        equal("consumPagat", data?.consumPagat?.toString()),
        data?.article &&
            exists(equal("liniesOperacio.article.id", data?.article?.id))
    ).toString();

    return filter;
};

const OperacioFilter = (props) => {
    const { onSpringFilterChange, onFilterInitialized } = props;
    const filterApiRef = React.useRef();

    return (
        <Box sx={{ mb: 2 }}>
            <Filter
                name={"O_FILTER"}
                resourceName={"operacio"}
                persistentState
                springFilterBuilder={springFilterBuilder}
                onSpringFilterChange={onSpringFilterChange}
                onFilterInitialized={onFilterInitialized}
                size="small"
                apiRef={filterApiRef}
                initialOnChangeRequest
            >
                <FilterContent filterApiRef={filterApiRef} />
            </Filter>
        </Box>
    );
};

const TotalsContent = (props) => {
    const { totals } = props;
    const { t } = useTranslation();
    const { currentLanguage } = useBaseAppContext();

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 5,
                mt: 1,
                mb: 2,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("consumTripulacio.totals.operacions")}
                </Typography>
                <Typography>
                    {numberFormat(
                        totals.totalOperacions,
                        null,
                        currentLanguage
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("consumTripulacio.totals.efectiu")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.importEfectiu,
                        "eur",
                        null,
                        currentLanguage
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("consumTripulacio.totals.targeta")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.importTargeta,
                        "eur",
                        null,
                        currentLanguage
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("consumTripulacio.totals.total")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.importTotal,
                        "eur",
                        null,
                        currentLanguage
                    )}
                </Typography>
            </Box>
        </Box>
    );
};

const ConsumTripulacio = () => {
    const { t } = useTranslation();
    const [fixedFilter, setFixedFilter] = React.useState();
    const [filterInitialized, setFilterInitialized] = React.useState(false);
    const [totals, setTotals] = React.useState();
    const { apiInitialized, exec: apiExec } = useResourceApi("operacio");
    const gridApiRef = React.useRef();
    const { currentLanguage } = useBaseAppContext();
    const navigate = useNavigate();

    const columns = [
        {
            name: "data",
            flex: 0.75,
            minWidth: 175,
        },
        {
            name: "numero",
            flex: 0.75,
            minWidth: 130,
        },
        {
            name: "ruta",
            flex: 1,
            minWidth: 250,
        },
        {
            name: "consumidorTripulant",
            flex: 1,
            minWidth: 250,
        },
        {
            name: "embarcacio",
            flex: 1,
            minWidth: 150,
        },
        {
            name: "user",
            flex: 1,
        },
        {
            name: "numeroLinies",
            flex: 0.75,
        },
        {
            name: "importTotal",
            flex: 0.5,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "consumPagat",
            flex: 0.75,
            minWidth: 150,
        },
    ];

    // Todo: S'ha posat aixo pq la llibreria no refresca la traduccio de l'accio sense refrescar el grid

    React.useEffect(() => {
        gridApiRef?.current?.doRefresh();
    }, [currentLanguage]);

    const obtenirTotals = () => {
        apiExec("exec_O_OBTENIR_TOTALS", {
            data: { springFilter: fixedFilter ?? undefined }, // TODO: Ha de poder rebre undefined com a filtre o text buid
            callbacks: {
                setResponse: (response) => {
                    const resource = {
                        ...response.data,
                        _resource: response,
                        _links: response.links,
                    };
                    setTotals(resource);
                },
                setError: (error) => {},
            },
        });
    };

    React.useEffect(() => {
        if (apiInitialized && filterInitialized) {
            obtenirTotals();
        }
    }, [apiInitialized, filterInitialized, fixedFilter]);

    const rowAdditionalActions = (params) => {
        const listActions = [
            {
                icon: "search", //visibility o search
                label: t("comu.detall"),
                onClick: () => navigate(`form/${params?.id}`),
            },
        ];

        if (params?.consumPagat) {
            listActions.push({
                icon: "closed",
                showInMenu: true,
                rowLinkAction: "O_MARCAR_CONSUM_NO_PAGAT",
                rowLinkActionOnSuccess: () => gridApiRef?.current?.doRefresh(),
            });
        } else {
            listActions.push({
                icon: "done",
                showInMenu: true,
                rowLinkAction: "O_MARCAR_CONSUM_PAGAT",
                rowLinkActionOnSuccess: () => gridApiRef?.current?.doRefresh(),
            });
        }

        return listActions;
    };

    return (
        <Box
            sx={{
                height: "100%",
            }}
        >
            <Grid
                apiRef={gridApiRef}
                title={t("consumTripulacio.title")}
                resourceName="operacio"
                fixedFilter={fixedFilter}
                findDisabled={!filterInitialized}
                columns={columns}
                rowHideEditViewAction
                formRoutePath="form"
                density="compact"
                onRefresh={() => obtenirTotals()}
                onDelete={() => obtenirTotals()}
                rowAdditionalActions={(params) =>
                    rowAdditionalActions(params.row)
                }
                toolbarAdditionalRow={
                    <>
                        <OperacioFilter
                            onSpringFilterChange={setFixedFilter}
                            onFilterInitialized={() =>
                                setFilterInitialized(true)
                            }
                        />
                        {totals && <TotalsContent totals={totals} />}
                    </>
                }
                defaultSortModel={[
                    {
                        field: "data",
                        sort: "desc",
                    },
                ]}
            />
        </Box>
    );
};
export default ConsumTripulacio;
